<template>
  <div class="force-password-backdrop">
    <div class="force-password-card">
      <!-- Glow Decorativo Crimson Velvet -->
      <div class="card-glow"></div>

      <!-- Ícone e Cabeçalho -->
      <div class="text-center mb-4 position-relative">
        <div class="shield-badge mb-3">
          <i class="uil uil-shield-exclamation text-caos-crimson"></i>
        </div>
        <h3 class="fw-bold text-white mb-2 font-cinzel text-glow-red">Redefinição Obrigatória</h3>
        <p class="text-white-50 small mb-0 px-2">
          Por diretriz de segurança, sua conta está com a credencial temporária padrão (<span class="badge bg-black border border-danger-subtle text-danger font-monospace">c405new</span>). 
          Defina uma nova senha pessoal para desbloquear o sistema.
        </p>
      </div>

      <!-- Formulário -->
      <form @submit.prevent="handleSubmit" class="position-relative">
        <!-- Feedback de Erro -->
        <div v-if="errorMessage" class="caos-alert-error py-2 px-3 small d-flex align-items-center mb-3">
          <i class="uil uil-exclamation-triangle me-2 fs-5 text-caos-crimson"></i>
          <div>{{ errorMessage }}</div>
        </div>

        <!-- Feedback de Sucesso -->
        <div v-if="successMessage" class="caos-alert-success py-2 px-3 small d-flex align-items-center mb-3">
          <i class="uil uil-check-circle me-2 fs-5 text-body-emphasis"></i>
          <div>{{ successMessage }}</div>
        </div>

        <!-- Campo: Senha Atual -->
        <div class="mb-3">
          <label class="form-label text-white-50 small fw-bold text-uppercase tracking-wider">Senha Atual / Temporária</label>
          <div class="input-group">
            <span class="input-group-text input-icon"><i class="uil uil-key-skeleton"></i></span>
            <input 
              :type="showCurrent ? 'text' : 'password'" 
              v-model="currentPassword" 
              class="form-control glass-input font-monospace" 
              placeholder="Digite c405new"
              required 
            />
            <button class="btn btn-outline-secondary btn-eye" type="button" @click="showCurrent = !showCurrent">
              <i :class="showCurrent ? 'uil uil-eye-slash' : 'uil uil-eye'"></i>
            </button>
          </div>
        </div>

        <!-- Campo: Nova Senha -->
        <div class="mb-3">
          <label class="form-label text-white-50 small fw-bold text-uppercase tracking-wider">Nova Senha Pessoal</label>
          <div class="input-group">
            <span class="input-group-text input-icon"><i class="uil uil-lock-alt"></i></span>
            <input 
              :type="showNew ? 'text' : 'password'" 
              v-model="newPassword" 
              class="form-control glass-input" 
              placeholder="Mínimo 6 caracteres"
              required 
            />
            <button class="btn btn-outline-secondary btn-eye" type="button" @click="showNew = !showNew">
              <i :class="showNew ? 'uil uil-eye-slash' : 'uil uil-eye'"></i>
            </button>
          </div>

          <!-- Indicador de Força de Senha (Estilo Vermelho & Preto) -->
          <div v-if="newPassword" class="mt-2">
            <div class="progress strength-track">
              <div 
                class="progress-bar transition-all" 
                :class="strengthBarClass" 
                :style="{ width: `${passwordStrength.score * 25}%` }"
              ></div>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-1">
              <span class="text-white-50" style="font-size: 0.7rem;">Força da senha:</span>
              <span class="fw-bold font-monospace" :class="strengthTextClass" style="font-size: 0.72rem;">
                {{ passwordStrength.label }}
              </span>
            </div>
          </div>
        </div>

        <!-- Campo: Confirmar Nova Senha -->
        <div class="mb-4">
          <label class="form-label text-white-50 small fw-bold text-uppercase tracking-wider">Confirmação da Nova Senha</label>
          <div class="input-group">
            <span class="input-group-text input-icon"><i class="uil uil-check-circle"></i></span>
            <input 
              :type="showConfirm ? 'text' : 'password'" 
              v-model="confirmPassword" 
              class="form-control glass-input" 
              placeholder="Repita a nova senha"
              required 
            />
            <button class="btn btn-outline-secondary btn-eye" type="button" @click="showConfirm = !showConfirm">
              <i :class="showConfirm ? 'uil uil-eye-slash' : 'uil uil-eye'"></i>
            </button>
          </div>
          <div v-if="confirmPassword && newPassword !== confirmPassword" class="text-caos-crimson mt-1" style="font-size: 0.75rem;">
            As senhas não coincidem.
          </div>
        </div>

        <!-- Botões de Ação -->
        <div class="d-grid gap-2">
          <button 
            type="submit" 
            class="btn btn-caos-crimson py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
            :disabled="isSubmitting || !isFormValid"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status"></span>
            <i v-else class="uil uil-check-circle"></i>
            <span>Salvar Nova Senha e Desbloquear</span>
          </button>

          <button 
            type="button" 
            @click="auth.logout('/login')" 
            class="btn btn-link text-white-50 text-decoration-none small py-1 mt-1 hover-crimson"
          >
            <i class="uil uil-signout me-1"></i> Desconectar e sair da conta
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAuthStore } from '../../stores/auth'

const auth = useAuthStore()

const currentPassword = ref('c405new')
const newPassword = ref('')
const confirmPassword = ref('')
const showCurrent = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)

const isSubmitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

// Avaliador de Força de Senha — Tons Vermelho & Preto
const passwordStrength = computed(() => {
  const p = newPassword.value
  if (!p) return { score: 0, label: 'Vazia' }
  if (p.length < 6) return { score: 1, label: 'Muito Curta (mín. 6)' }
  
  let score = 1
  if (p.length >= 8) score++
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++
  if (/[0-9]/.test(p) || /[^A-Za-z0-9]/.test(p)) score++

  if (score <= 2) return { score: 2, label: 'Fraca' }
  if (score === 3) return { score: 3, label: 'Boa' }
  return { score: 4, label: 'Forte & Segura' }
})

const strengthBarClass = computed(() => {
  const s = passwordStrength.value.score
  if (s <= 1) return 'strength-bar-1'
  if (s === 2) return 'strength-bar-2'
  if (s === 3) return 'strength-bar-3'
  return 'strength-bar-4'
})

const strengthTextClass = computed(() => {
  const s = passwordStrength.value.score
  if (s <= 1) return 'text-strength-1'
  if (s === 2) return 'text-strength-2'
  if (s === 3) return 'text-strength-3'
  return 'text-strength-4'
})

const isFormValid = computed(() => {
  return (
    currentPassword.value.trim().length > 0 &&
    newPassword.value.trim().length >= 6 &&
    newPassword.value !== currentPassword.value &&
    newPassword.value === confirmPassword.value
  )
})

async function handleSubmit() {
  errorMessage.value = ''
  successMessage.value = ''

  if (newPassword.value.length < 6) {
    errorMessage.value = 'A nova senha deve possuir no mínimo 6 caracteres.'
    return
  }

  if (newPassword.value === currentPassword.value) {
    errorMessage.value = 'A nova senha deve ser diferente da senha temporária (c405new).'
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'A confirmação de senha não coincide com a nova senha.'
    return
  }

  isSubmitting.value = true
  try {
    await auth.changePassword(currentPassword.value, newPassword.value)
    successMessage.value = 'Senha redefinida com sucesso! Acesso liberado.'
    setTimeout(() => {
      // O v-if no App.vue removerá o modal automaticamente quando auth.mustChangePassword for falso
    }, 800)
  } catch (err) {
    errorMessage.value = err.response?.data?.error || 'Erro ao alterar a senha. Verifique a senha atual informada.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.force-password-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(8, 8, 12, 0.92);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeInBackdrop 0.3s ease-out;
}

.force-password-card {
  position: relative;
  width: 100%;
  max-width: 480px;
  background: linear-gradient(175deg, #161418 0%, #0d0c0e 100%);
  border: 1px solid rgba(182, 19, 35, 0.38);
  border-radius: 24px;
  padding: 36px 30px;
  box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.95), 0 0 35px rgba(182, 19, 35, 0.18);
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  height: 160px;
  background: radial-gradient(circle, rgba(182, 19, 35, 0.32) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
}

.shield-badge {
  width: 70px;
  height: 70px;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(182, 19, 35, 0.12);
  border: 1px solid rgba(182, 19, 35, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  box-shadow: 0 0 30px rgba(182, 19, 35, 0.3);
}

.text-caos-crimson {
  color: #ff2a3b !important;
}

.text-glow-red {
  text-shadow: 0 0 18px rgba(182, 19, 35, 0.5);
}

.tracking-wider {
  letter-spacing: 0.8px;
}

.glass-input {
  background: rgba(22, 22, 28, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #fff !important;
  font-size: 0.95rem;
  padding: 10px 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.glass-input:focus {
  background: rgba(26, 26, 34, 0.95) !important;
  border-color: #b61323 !important;
  box-shadow: 0 0 0 0.25rem rgba(182, 19, 35, 0.3) !important;
}

.input-icon {
  background: rgba(18, 18, 24, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-right: none !important;
  color: rgba(255, 255, 255, 0.45);
}

.btn-eye {
  background: rgba(18, 18, 24, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-left: none !important;
  color: rgba(255, 255, 255, 0.5) !important;
  transition: color 0.2s;
}

.btn-eye:hover {
  color: #ff2a3b !important;
}

/* Força de Senha - Monocromático Vermelho & Preto */
.strength-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
}

.strength-bar-1 {
  background: #6e0c15;
}
.strength-bar-2 {
  background: #9c101e;
}
.strength-bar-3 {
  background: #c9182b;
}
.strength-bar-4 {
  background: #ff2a3b;
  box-shadow: 0 0 10px rgba(255, 42, 59, 0.6);
}

.text-strength-1 {
  color: #8c101b;
}
.text-strength-2 {
  color: #b61323;
}
.text-strength-3 {
  color: #e02436;
}
.text-strength-4 {
  color: #ff3a4c;
}

/* Botão Principal Crimson */
.btn-caos-crimson {
  background: linear-gradient(135deg, #b61323 0%, #7a0c17 100%);
  border: 1px solid rgba(255, 70, 90, 0.35);
  color: #ffffff;
  padding: 11px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(182, 19, 35, 0.5), 0 0 15px rgba(182, 19, 35, 0.2);
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
}

.btn-caos-crimson:hover:not(:disabled) {
  background: linear-gradient(135deg, #d3162a 0%, #940e1c 100%);
  box-shadow: 0 12px 30px -4px rgba(182, 19, 35, 0.7), 0 0 20px rgba(182, 19, 35, 0.35);
  transform: translateY(-1px);
  color: #fff;
}

.btn-caos-crimson:active:not(:disabled) {
  transform: translateY(0);
}

.btn-caos-crimson:disabled {
  background: rgba(45, 18, 22, 0.7);
  border-color: rgba(182, 19, 35, 0.2);
  color: rgba(255, 255, 255, 0.35);
  box-shadow: none;
}

.caos-alert-error {
  background: rgba(182, 19, 35, 0.16);
  border: 1px solid rgba(182, 19, 35, 0.45);
  border-radius: 10px;
  color: #ff8591;
}

.caos-alert-success {
  background: rgba(30, 60, 40, 0.35);
  border: 1px solid rgba(75, 181, 67, 0.4);
  border-radius: 10px;
  color: #8ce196;
}

.hover-crimson:hover {
  color: #ff4757 !important;
}

.font-cinzel {
  font-family: 'Cinzel', serif;
  letter-spacing: 0.5px;
}

@keyframes fadeInBackdrop {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
</style>
